/**
 * JavaScript version of zoom configuration with working bypass mechanism
 */

// Maximum timeframe for zoom-out (10 years in milliseconds)
export const MAX_ZOOM_TIMEFRAME = 10 * 365 * 24 * 60 * 60 * 1000;

// Default Y-axis maximum cap for initial view (can be exceeded with zoom)
export const INITIAL_Y_MAX = 300000;

// Zoom factors
export const ZOOM_IN_FACTOR = 0.9;  // For zooming in
export const ZOOM_OUT_FACTOR = 1.1; // For zooming out

// ✅ SIMPLE: Global bypass flags
window.bypassYConstraints = false;
window.bypassXConstraints = false;

// ✅ SIMPLE: Functions to control bypass behavior
export const setYConstraintsBypass = (bypass) => {
  window.bypassYConstraints = bypass;
};

export const setXConstraintsBypass = (bypass) => {
  window.bypassXConstraints = bypass;
};

/**
 * Handle wheel zoom event on the chart
 * JAVASCRIPT VERSION with working constraints bypass
 */
export const handleWheelZoom = (
  event,
  mouseX,
  xScale,
  yScale,
  initialScales,
  processedData,
  updateChart
) => {
  if (!initialScales) return;
  
  event.preventDefault();
  
  // Get current domains
  const [xStart, xEnd] = xScale.domain();
  const [yStart, yEnd] = yScale.domain();
  
  // Calculate zoom factors
  const direction = event.deltaY > 0 ? 1 : -1;
  const factor = direction > 0 ? 1.1 : 0.9;
  
  // Determine time at mouse position
  const mouseTime = xScale.invert(mouseX);
  
  // Calculate normalized position of the mouse
  const normalizedX = (mouseTime.getTime() - xStart.getTime()) / (xEnd.getTime() - xStart.getTime());
  
  // Calculate new time range
  const newTimeWidth = (xEnd.getTime() - xStart.getTime()) * factor;
  
  // Calculate new start and end times
  const newXStart = new Date(mouseTime.getTime() - newTimeWidth * normalizedX);
  const newXEnd = new Date(mouseTime.getTime() + newTimeWidth * (1 - normalizedX));
  
  // ✅ FIXED: Check global bypass flags
  if (!window.bypassXConstraints) {
    // Only apply constraints during manual zoom, not programmatic changes
    const earliestDate = Math.min(...processedData.map(d => d.time.getTime()));
    const earliestDateObj = new Date(earliestDate);
    
    if (newXStart < earliestDateObj) {
      const timeWidth = newXEnd.getTime() - newXStart.getTime();
      const fixedStart = earliestDateObj;
      const fixedEnd = new Date(fixedStart.getTime() + timeWidth);
      xScale.domain([fixedStart, fixedEnd]);
    } else {
      xScale.domain([newXStart, newXEnd]);
    }
  } else {
    // When bypassing, allow any X domain
    xScale.domain([newXStart, newXEnd]);
  }
  
  // ✅ FIXED: Check global bypass flags for Y axis
  const yRange = yEnd - yStart;
  const newYRange = yRange * factor;
  
  // Keep the scaling centered within the current viewpoint
  const newYMin = yStart - ((newYRange - yRange) / 2);
  const newYMax = yEnd + ((newYRange - yRange) / 2);
  
  if (!window.bypassYConstraints) {
    // ✅ SIMPLE FIX: Only constrain extreme zoom-out, allow all zoom-in
    if (newYMin < -2000 && direction > 0) {
      // Prevent zooming out to extremely negative values
      yScale.domain([-2000, newYMax + ((-2000) - newYMin)]);
    } else {
      // Allow all zoom-in operations and reasonable zoom-out
      yScale.domain([newYMin, newYMax]);
    }
  } else {
    // When bypassing, allow any Y domain (including very negative values)
    yScale.domain([newYMin, newYMax]);
  }
  
  // Apply changes
  updateChart();
};

// ✅ SIMPLE: Helper function to temporarily bypass constraints
export const withConstraintsBypass = (callback) => {
  const originalYBypass = window.bypassYConstraints;
  const originalXBypass = window.bypassXConstraints;
  
  
  try {
    window.bypassYConstraints = true;
    window.bypassXConstraints = true;
    callback();
  } finally {
    window.bypassYConstraints = originalYBypass;
    window.bypassXConstraints = originalXBypass;
  }
};

// ✅ ADD: Missing function that's being imported
export const withPredictionViewportAdjustment = (callback) => {
  const originalYBypass = window.bypassYConstraints;
  const originalXBypass = window.bypassXConstraints;
  
  
  try {
    // Enable both X and Y bypass for prediction adjustments
    window.bypassYConstraints = true;
    window.bypassXConstraints = true;
    callback();
  } finally {
    // Restore original state
    window.bypassYConstraints = originalYBypass;
    window.bypassXConstraints = originalXBypass;
  }
};
